/**
 * Tipi e classi di errore condivisi fra il client HTTP e il transport simulato.
 *
 * Stanno in un modulo separato per evitare un ciclo di import: cvChatClient
 * delega a mockTransport, e mockTransport ha bisogno degli stessi tipi.
 */

export type ChatRole = 'user' | 'assistant';

/**
 * Registro della chat, allineato al tema del sito:
 * business -> formal, casual -> informal.
 *
 * E' un enum e non testo libero: il prompt delle due personalita' vive sul
 * server. Se il client potesse inviare istruzioni, chiunque potrebbe riscrivere
 * il system prompt con una chiamata HTTP.
 */
export type ChatPersona = 'formal' | 'informal';

export interface ChatMessage {
  role: ChatRole;
  content: string;
}

export interface RetrievedSource {
  id: string;
  title: string;
  score: number;
}

export interface BudgetSnapshot {
  month: string;
  used_tokens: number;
  cap_tokens: number;
  remaining_tokens: number;
  exhausted: boolean;
  persistent: boolean;
}

export interface ChatMeta {
  subject: string;
  contact_url: string;
  suggested_questions: string[];
  rate_limit: string;
  rate_limit_window_seconds: number;
  budget: BudgetSnapshot;
  max_input_chars: number;
}

/** Eventi emessi dal backend sul canale SSE. */
export type StreamEvent =
  | { type: 'token'; text: string }
  | { type: 'sources'; items: RetrievedSource[] }
  | { type: 'info'; code: string; message?: string; flags?: string[]; removed?: string[] }
  | { type: 'guard'; decision: string; categories?: string[]; stage?: string }
  | { type: 'budget_exhausted'; message: string }
  | { type: 'done'; budget?: BudgetSnapshot }
  | { type: 'error'; code: string; message: string };

export class ChatError extends Error {
  constructor(
    message: string,
    readonly code: 'rate_limited' | 'forbidden' | 'network' | 'server' | 'aborted' | 'unknown',
    /** Secondi di attesa comunicati dal server su un 429. */
    readonly retryAfterSeconds?: number
  ) {
    super(message);
    this.name = 'ChatError';
  }
}

export interface StreamChatOptions {
  messages: ChatMessage[];
  persona: ChatPersona;
  signal: AbortSignal;
  onEvent: (event: StreamEvent) => void;
}
